import axios from "axios";
import { getCareersClient } from "../Services/ClientService";
import {
  removeCareersClient,
  updateCareersClient,
} from "../State/Slices/SelectedCareersClientSlice";
import { updateClient } from "../State/Slices/SelectedClientSlice";
import { AppDispatch } from "../State/Store";
import { IClient } from "../Typings/Client";

interface SelectBaseClientOptions {
  /** Called when the client has careers but the careers lookup failed. */
  onCareersError?: (error: unknown) => void;
}

/**
 * Selects a base client and, when it has a careers configuration, loads and
 * stores its careers client.
 *
 * The base client is committed to Redux up-front — before the careers lookup is
 * awaited — so that a transient failure of `getCareersClient()` can never leave
 * the app with no selected client. That "no selected client" state is what
 * previously left the dashboard stuck rendering skeletons with a blank
 * navigation menu, because a single failed/dropped careers request in the login
 * bootstrap chain aborted client selection entirely.
 *
 * @returns `true` when selection completed fully (including careers where
 *   applicable); `false` when the careers lookup failed (the base client is
 *   still selected and the portal remains usable).
 */
export async function selectBaseClient(
  baseClient: IClient,
  dispatch: AppDispatch,
  { onCareersError }: SelectBaseClientOptions = {}
): Promise<boolean> {
  axios.defaults.headers.common["baseClientId"] = baseClient.id;

  // Commit the base client immediately so the portal stays usable regardless of
  // what happens with the careers lookup below.
  dispatch(updateClient(baseClient));

  if (!baseClient.hasCareers) {
    dispatch(removeCareersClient());
    delete axios.defaults.headers.common["careersClientId"];
    return true;
  }

  try {
    const careersClient = await getCareersClient();
    axios.defaults.headers.common["careersClientId"] = careersClient.id;
    dispatch(updateCareersClient(careersClient));
    return true;
  } catch (error) {
    // Keep the base client selected; just drop any stale careers context so we
    // don't send a mismatched careersClientId header for the new client.
    console.error("Error fetching careers client:", error);
    dispatch(removeCareersClient());
    delete axios.defaults.headers.common["careersClientId"];
    onCareersError?.(error);
    return false;
  }
}
